import React, { useState, useEffect } from 'react';
import MultiSelectDropdown from './multi-select';
import { lookupService } from '../services/lookups.service';
import Accordion from './Accordion';

interface ReportsFilterOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onClearAll: () => void;
  activeFilters: {
    sector: string[];
    province: string[];
    status: string[];
    startDate: string;
    endDate: string;
    search: string;
  };
  onApply: (filters: {
    sector: string[];
    province: string[];
    status: string[];
    startDate: string;
    endDate: string;
    search: string;
  }) => void;
}

interface Option {
  id: string;
  name: string;
}

const ReportsFilterOverlay: React.FC<ReportsFilterOverlayProps> = ({
  isOpen,
  onClose,
  onClearAll,
  activeFilters,
  onApply,
}) => {
  // Add state for expanded sections
  const [expandedSections, setExpandedSections] = useState({
    dateRange: true,
    sector: false,
    province: false,
    status: false,
  });

  // Add state for all sections
  const [sectors, setSectors] = useState<Option[]>([]);
  const [selectedSectors, setSelectedSectors] = useState<string[]>(activeFilters.sector || []);

  const [provinces, setProvinces] = useState<Option[]>([]);
  const [selectedProvinces, setSelectedProvinces] = useState<string[]>(activeFilters.province || []);

  const [selectedStatus, setSelectedStatus] = useState<string[]>(activeFilters.status || []);
  
  // Format date to ISO 8601
  const formatToISO = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Parse ISO date to local date format for input
  const parseFromISO = (isoString: string) => {
    if (!isoString) return '';
    return isoString.split('T')[0];
  };

  const [startDate, setStartDate] = useState(parseFromISO(activeFilters.startDate) || '');
  const [endDate, setEndDate] = useState(parseFromISO(activeFilters.endDate) || '');

  // Fetch sectors
  useEffect(() => {
    const fetchSectors = async () => {
      try {
        const sectorsData = await lookupService.getSectors();
        const formattedSectors = sectorsData.map(sector => ({
          id: sector.id.toString(),
          name: sector.name
        }));
        setSectors(formattedSectors);
      } catch (error) {
        console.error('Error fetching sectors:', error);
      }
    };

    fetchSectors();
  }, []);

  // Fetch provinces
  useEffect(() => {
    const fetchProvinces = async () => {
      try {
        const provincesData = await lookupService.getProvinces();
        const formattedProvinces = provincesData.map(province => ({
          id: province.id.toString(),
          name: province.name
        }));
        setProvinces(formattedProvinces);
      } catch (error) {
        console.error('Error fetching provinces:', error);
      }
    };

    fetchProvinces();
  }, []);

  // Effect to update selected values when sectors are loaded
  useEffect(() => {
    if (sectors.length > 0 && activeFilters.sector.length > 0) {
      setSelectedSectors(activeFilters.sector);
    }
  }, [sectors, activeFilters.sector]);

  // Effect to update selected values when provinces are loaded
  useEffect(() => {
    if (provinces.length > 0 && activeFilters.province.length > 0) {
      setSelectedProvinces(activeFilters.province);
    }
  }, [provinces, activeFilters.province]);

  // Add useEffect to handle body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const toggleSection = (section: 'dateRange' | 'sector' | 'province' | 'status') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Update sector selection handler
  const handleSectorSelect = (selectedIds: string[]) => {
    setSelectedSectors(selectedIds);
  };

  // Update province selection handler
  const handleProvinceSelect = (selectedIds: string[]) => {
    setSelectedProvinces(selectedIds);
  };

  // Reset function
  const resetFilters = () => {
    setSelectedSectors([]);
    setSelectedProvinces([]);
    setSelectedStatus([]);
    setStartDate('');
    setEndDate('');
  };

  // Handle clear all
  const handleClearAll = () => {
    resetFilters();
    onClearAll();
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    onApply({
      sector: selectedSectors,
      province: selectedProvinces,
      status: selectedStatus,
      startDate: startDate ? formatToISO(startDate) : '',
      endDate: endDate ? formatToISO(endDate) : '',
      search: activeFilters.search,
    });
    onClose();
  };

  return (
    <div
      className={`fixed inset-0 bg-[#A42823] transition-all duration-300 ease-in-out ${
        isOpen ? 'bg-black bg-opacity-50' : 'bg-black bg-opacity-0 pointer-events-none'
      } z-50`}
    >
      <div
        className={`fixed right-0 bg-white h-screen w-full md:w-[500px] shadow-lg transition-all duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">Filter by</h2>
          <button
            id="filter-close-button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
            data-testid="filter-close-button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="p-4 overflow-y-auto h-[calc(100vh-180px)]">
          <div className="space-y-4">
            {/* Date Range Section */}
            <div id="date-range-section">
              <Accordion 
                title="Date Range" 
                isExpanded={expandedSections.dateRange}
                onToggle={() => toggleSection('dateRange')}
              >
                <div className="space-y-3 mt-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Start Date</label>
                    <input
                      id="start-date-input"
                      data-testid="start-date-input"
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      value={startDate}
                      max={endDate || undefined}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">End Date</label>
                    <input
                      id="end-date-input"
                      data-testid="end-date-input"
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      value={endDate}
                      min={startDate || undefined}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>
              </Accordion>
            </div>

            {/* Sector Section */}
            <div id="sector-section">
              <Accordion
                title="Sector"
                isExpanded={expandedSections.sector}
                onToggle={() => toggleSection('sector')}
              >
                <div id="sector-dropdown-container" data-testid="sector-dropdown-container">
                  <MultiSelectDropdown
                    options={sectors}
                    onSelect={handleSectorSelect}
                    getValue={(option) => option.id}
                    getDisplayText={(option) => option.name}
                    selectedValues={selectedSectors}
                    placeholder="Select sectors"
                  />
                </div>
              </Accordion>
            </div>

            {/* Province Section */}
            <div id="province-section">
              <Accordion
                title="Province"
                isExpanded={expandedSections.province}
                onToggle={() => toggleSection('province')}
              >
                <div id="province-dropdown-container" data-testid="province-dropdown-container">
                  <MultiSelectDropdown
                    options={provinces}
                    onSelect={handleProvinceSelect}
                    getValue={(option) => option.id}
                    getDisplayText={(option) => option.name}
                    selectedValues={selectedProvinces}
                    placeholder="Select provinces"
                  />
                </div>
              </Accordion>
            </div>

            {/* Status Section */}
            <div id="status-section">
              <Accordion
                title="Jobsite Status"
                isExpanded={expandedSections.status}
                onToggle={() => toggleSection('status')}
              >
                <label className="flex items-center gap-2">
                  <input
                    id="status-active-checkbox"
                    data-testid="status-active-checkbox"
                    type="checkbox"
                    className="custom-checkbox"
                    checked={selectedStatus.includes('active')}
                    onChange={(e) => {
                      setSelectedStatus(prev => {
                        if (e.target.checked) {
                          return [...prev, 'active'];
                        } else {
                          return prev.filter(status => status !== 'active');
                        }
                      });
                    }}
                  />
                  <span>Active</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    id="status-inactive-checkbox"
                    data-testid="status-inactive-checkbox"
                    type="checkbox"
                    className="custom-checkbox"
                    checked={selectedStatus.includes('inactive')}
                    onChange={(e) => {
                      setSelectedStatus(prev => {
                        if (e.target.checked) {
                          return [...prev, 'inactive'];
                        } else {
                          return prev.filter(status => status !== 'inactive');
                        }
                      });
                    }}
                  />
                  <span>Inactive</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    id="status-completed-checkbox"
                    data-testid="status-completed-checkbox"
                    type="checkbox"
                    className="custom-checkbox"
                    checked={selectedStatus.includes('completed')}
                    onChange={(e) => {
                      setSelectedStatus(prev => {
                        if (e.target.checked) {
                          return [...prev, 'completed'];
                        } else {
                          return prev.filter(status => status !== 'completed');
                        }
                      });
                    }}
                  />
                  <span>Completed</span>
                </label>
              </Accordion>
            </div>
          </div>
        </div>

        {/* Fixed Bottom Buttons */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t">
          <div className="flex items-center justify-end gap-3">
            <button
              className="btn-secondary"
              onClick={handleClearAll}
              id="clear-filters-button"
              data-testid="clear-filters-button"
            >
              Clear All
            </button>
            <button
              className="btn-primary"
              onClick={handleApplyFilters}
              id="apply-filters-overlay-button"
              data-testid="apply-filters-button"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsFilterOverlay; 